import React, { useState, useCallback } from 'react';
import { useLazyCommand, useEvent } from '@soap/modules';
import { X } from 'lucide-react';

interface UnarchiveStudentDialogProps {
  studentId: string;
  studentName: string;
  onClose: () => void;
}

export function UnarchiveStudentDialog({ studentId, studentName, onClose }: UnarchiveStudentDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [sendCommand] = useLazyCommand('C300v1_UnarchiveStudent');

  useEvent({
    eventName: "E137v1_StudentUnarchived",
    onEventReceived: (event: any) => {
      if (submitting && event.e137_StudentId === studentId) {
        setSubmitting(false);
        onClose();
      }
    }
  }, [submitting, studentId, onClose]);

  const handleConfirm = () => {
    setSubmitting(true);
    sendCommand({
      $type: 'C300v1_UnarchiveStudent',
      C300_StudentId: studentId
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-2xl font-bold text-gray-900">Unarchive Student</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={submitting}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <p className="text-gray-700">
              Please confirm you want to unarchive the student <strong>{studentName}</strong>?
            </p>
            <div className="bg-action-blue/10 p-4 rounded-lg border border-action-blue/30">
              <h4 className="font-medium text-action-blue-dark mb-2">Unarchived Students</h4>
              <ul className="ml-4 space-y-1">
                <li className="text-sm text-action-blue-dark">• count towards your student limit</li>
                <li className="text-sm text-action-blue-dark">• cannot be unarchived if you do not have available student capacity</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-5">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={submitting}
              className="px-6 py-2 bg-action-blue hover:bg-action-blue-dark text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {submitting ? 'Unarchiving...' : 'Unarchive'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
